var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { isBefore, isPast } from "date-fns";
import { IdentifiedPatientState, primaryConsentTypes } from "healthex-common/consent.dtos";
import { ContactMethod } from "healthex-common/consentconfig.dtos";
import { PatientDataVisualizationPdfStatus } from "healthex-common/patient.data.visualization.dtos";
import { UserType } from "healthex-common/user.dtos";
import { computed, observable, reaction } from "mobx";
import { NOT_LOADED } from "../Infra/RemoteData";
import { NamedRoutes } from "./NamedRoutes";
import { NavigationHelper } from "./NavigationHelper";
import { findMatchingNamedRouteParent } from "./RoutingUIManager";
const _patientResearchDashboardRoutes = {
    RESEARCH: NamedRoutes.PATIENT_SHARING,
    MY_HEALTHEX: NamedRoutes.PATIENT_HOME,
};
const patientResearchDashboardRoutes = _patientResearchDashboardRoutes;
export var MobileBottomNavigationButtonType;
(function (MobileBottomNavigationButtonType) {
    MobileBottomNavigationButtonType[MobileBottomNavigationButtonType["PROFILE"] = 0] = "PROFILE";
    MobileBottomNavigationButtonType[MobileBottomNavigationButtonType["RESEARCH"] = 1] = "RESEARCH";
})(MobileBottomNavigationButtonType || (MobileBottomNavigationButtonType = {}));
export class PatientResearchDashboardUIManager {
    constructor(routingUIManager, studyManager, shlUIManager, shlService, documentRetrievalManager, authManager, patientPreferencesManager) {
        this.routingUIManager = routingUIManager;
        this.studyManager = studyManager;
        this.shlUIManager = shlUIManager;
        this.shlService = shlService;
        this.documentRetrievalManager = documentRetrievalManager;
        this.authManager = authManager;
        this.patientPreferencesManager = patientPreferencesManager;
        this.patientResearchDashboardNavigationHelper = new NavigationHelper(this.routingUIManager, patientResearchDashboardRoutes);
        this.mostRecentRetrieval = NOT_LOADED;
        this.pollRetrievalInterval = null;
        reaction(() => this.isOnPatientSharingPage, async (isOnPatientSharing) => {
            if (isOnPatientSharing)
                await this.refreshStudiesAndPatientSHLs();
        }, { fireImmediately: true });
        reaction(() => [this.authManager.isLoggedIn, this.isMyHealthExPage], async () => {
            await this.loadMostRecentRetrieval();
        });
        reaction(() => this.isUnifiedRecordProcessing, async () => {
            if (this.pollRetrievalInterval !== null) {
                window.clearInterval(this.pollRetrievalInterval);
                this.pollRetrievalInterval = null;
            }
            if (this.isUnifiedRecordProcessing === true) {
                this.pollRetrievalInterval = window.setInterval(async () => {
                    await this.loadMostRecentRetrieval();
                }, 10000);
            }
        }, { fireImmediately: true });
    }
    async loadMostRecentRetrieval() {
        if (!this.isMyHealthExPage)
            return;
        if (!this.authManager.isLoggedIn || this.authManager.userType !== UserType.PATIENT) {
            this.mostRecentRetrieval = null;
            return;
        }
        this.mostRecentRetrieval = await this.documentRetrievalManager.getMostRecentDownloadableOrProcessingTaskBatch();
    }
    get hasUnifiedRecordExpired() {
        var _a;
        if (this.mostRecentRetrieval === NOT_LOADED)
            return NOT_LOADED;
        return ((_a = this.mostRecentRetrieval) === null || _a === void 0 ? void 0 : _a.patientDeliveryExpiration) && isPast(this.mostRecentRetrieval.patientDeliveryExpiration);
    }
    get unifiedRecordSummary() {
        var _a;
        if (this.mostRecentRetrieval === NOT_LOADED)
            return NOT_LOADED;
        if (!this.mostRecentRetrieval)
            return null;
        return (_a = this.mostRecentRetrieval.patientData) !== null && _a !== void 0 ? _a : null;
    }
    get retrievalContactMethod() {
        var _a;
        if (this.mostRecentRetrieval === NOT_LOADED)
            return NOT_LOADED;
        if (!this.mostRecentRetrieval)
            return null;
        return (_a = this.mostRecentRetrieval.patientContactMethod) !== null && _a !== void 0 ? _a : null;
    }
    get preferredContactMethod() {
        var _a, _b;
        if (this.retrievalContactMethod !== NOT_LOADED && this.retrievalContactMethod)
            return this.retrievalContactMethod;
        if (this.patientPreferencesManager.preferences.value === NOT_LOADED)
            return NOT_LOADED;
        return (_b = (_a = this.patientPreferencesManager.preferences.value) === null || _a === void 0 ? void 0 : _a.preferredContactMethod) !== null && _b !== void 0 ? _b : ContactMethod.EMAIL;
    }
    get contactMethodWhenUnifiedRecordSent() {
        var _a, _b;
        if (this.mostRecentRetrieval === NOT_LOADED)
            return NOT_LOADED;
        return (_b = (_a = this.mostRecentRetrieval) === null || _a === void 0 ? void 0 : _a.patientContactMethod) !== null && _b !== void 0 ? _b : null;
    }
    get isUnifiedRecordDownloadable() {
        var _a;
        if (this.mostRecentRetrieval === NOT_LOADED || this.unifiedRecordSummary === NOT_LOADED)
            return NOT_LOADED;
        return ((_a = this.mostRecentRetrieval) === null || _a === void 0 ? void 0 : _a.pdfStatus) === PatientDataVisualizationPdfStatus.COMPLETED && !!this.unifiedRecordSummary;
    }
    get isUnifiedRecordProcessing() {
        if (this.isUnifiedRecordDownloadable === NOT_LOADED)
            return NOT_LOADED;
        if (!this.mostRecentRetrieval)
            return false;
        return !this.isUnifiedRecordDownloadable;
    }
    get isMyHealthExPage() {
        return this.selectedBottomNavigationButton === MobileBottomNavigationButtonType.PROFILE;
    }
    get isOnPatientSharingPage() {
        if (this.authManager.userType !== "PATIENT")
            return false;
        return !!findMatchingNamedRouteParent(this.routingUIManager.currentRoute, [NamedRoutes.PATIENT_SHARING]);
    }
    get selectedBottomNavigationButton() {
        switch (this.patientResearchDashboardNavigationHelper.currentNavigationItem) {
            case "RESEARCH":
                return MobileBottomNavigationButtonType.RESEARCH;
            case "MY_HEALTHEX":
                return MobileBottomNavigationButtonType.PROFILE;
            default:
        }
        return null;
    }
    get noConsentsAvailable() {
        if (this.studyManager.allPatientConsentsByStudyForSearchTotals.value === NOT_LOADED)
            return NOT_LOADED;
        return this.studyManager.allPatientConsentsByStudyForSearchTotals.value.all === 0;
    }
    get sortedStudies() {
        if (this.currentStudies === NOT_LOADED)
            return NOT_LOADED;
        const withMultipleEntriesPerStudy = [];
        this.currentStudies.forEach((studyWithConsents) => {
            const { consents, ...study } = studyWithConsents;
            const pendingConsents = consents.filter((consent) => consent.pending);
            const consentedConsents = consents.filter((consent) => !consent.pending);
            if (pendingConsents.length > 0) {
                withMultipleEntriesPerStudy.push({ study, consents: pendingConsents });
            }
            if (consentedConsents.length > 0) {
                withMultipleEntriesPerStudy.push({ study, consents: consentedConsents });
            }
        });
        return (withMultipleEntriesPerStudy
            .sort((a, b) => {
            var _a, _b;
            const aTimestamp = (_a = a.consents.find((consent) => primaryConsentTypes.includes(consent.consentType))) === null || _a === void 0 ? void 0 : _a.timestamp;
            const bTimestamp = (_b = b.consents.find((consent) => primaryConsentTypes.includes(consent.consentType))) === null || _b === void 0 ? void 0 : _b.timestamp;
            if (aTimestamp && bTimestamp) {
                return isBefore(new Date(aTimestamp), new Date(bTimestamp)) ? 1 : -1;
            }
            return 0;
        })
            .sort((a, b) => {
            var _a, _b;
            const isAEnrolling = ((_a = a.consents.find((consent) => primaryConsentTypes.includes(consent.consentType))) === null || _a === void 0 ? void 0 : _a.state) ===
                IdentifiedPatientState.ENROLLING;
            const isBEnrolling = ((_b = b.consents.find((consent) => primaryConsentTypes.includes(consent.consentType))) === null || _b === void 0 ? void 0 : _b.state) ===
                IdentifiedPatientState.ENROLLING;
            if (isAEnrolling && isBEnrolling)
                return 0;
            if (isAEnrolling && !isBEnrolling)
                return -1;
            if (!isAEnrolling && isBEnrolling)
                return 1;
            return 0;
        }));
    }
    get currentStudies() {
        if (!this.isOnPatientSharingPage)
            return NOT_LOADED;
        return this.studyManager.allPatientConsentsByStudyForSearch.value;
    }
    async refreshStudiesAndPatientSHLs() {
        if (!this.isOnPatientSharingPage)
            return;
        await Promise.all([
            this.studyManager.allPatientConsentsByStudyForSearch.waitForValue(),
            this.shlUIManager.patientSHLsForSearch.waitForValue(),
        ]);
    }
    goToPage(value) {
        switch (value) {
            case MobileBottomNavigationButtonType.RESEARCH:
                this.patientResearchDashboardNavigationHelper.redirectTo("RESEARCH");
                break;
            case MobileBottomNavigationButtonType.PROFILE:
                this.patientResearchDashboardNavigationHelper.navigateTo("MY_HEALTHEX");
                break;
        }
    }
    async getDownloadPdfLink() {
        var _a;
        if (this.mostRecentRetrieval === NOT_LOADED || this.mostRecentRetrieval === null)
            return null;
        return await this.documentRetrievalManager.getDownloadPdfLink((_a = this.mostRecentRetrieval) === null || _a === void 0 ? void 0 : _a.batchId);
    }
    async getDownloadRawDocumentsLink() {
        var _a;
        if (this.mostRecentRetrieval === NOT_LOADED || this.mostRecentRetrieval === null)
            return null;
        return await this.documentRetrievalManager.getDownloadDocumentsLink((_a = this.mostRecentRetrieval) === null || _a === void 0 ? void 0 : _a.batchId);
    }
}
__decorate([
    observable
], PatientResearchDashboardUIManager.prototype, "mostRecentRetrieval", void 0);
__decorate([
    computed
], PatientResearchDashboardUIManager.prototype, "hasUnifiedRecordExpired", null);
__decorate([
    computed
], PatientResearchDashboardUIManager.prototype, "unifiedRecordSummary", null);
__decorate([
    computed
], PatientResearchDashboardUIManager.prototype, "retrievalContactMethod", null);
__decorate([
    computed
], PatientResearchDashboardUIManager.prototype, "preferredContactMethod", null);
__decorate([
    computed
], PatientResearchDashboardUIManager.prototype, "contactMethodWhenUnifiedRecordSent", null);
__decorate([
    computed
], PatientResearchDashboardUIManager.prototype, "isUnifiedRecordDownloadable", null);
__decorate([
    computed
], PatientResearchDashboardUIManager.prototype, "isMyHealthExPage", null);
__decorate([
    computed
], PatientResearchDashboardUIManager.prototype, "isOnPatientSharingPage", null);
__decorate([
    computed
], PatientResearchDashboardUIManager.prototype, "selectedBottomNavigationButton", null);
__decorate([
    computed
], PatientResearchDashboardUIManager.prototype, "noConsentsAvailable", null);
__decorate([
    computed
], PatientResearchDashboardUIManager.prototype, "sortedStudies", null);
__decorate([
    computed
], PatientResearchDashboardUIManager.prototype, "currentStudies", null);
